import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Activity, Users, Calendar, CreditCard, Flag, LifeBuoy } from 'lucide-react';
import { HeartbeatHourDetailModal } from './HeartbeatHourDetailModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Each metric maps to the admin tab where the admin can act on it.
// Click-through wires the heartbeat into the rest of the panel so the
// chart stops being a "look" tool and becomes a "navigate" tool.
const METRIC_COLORS = {
  guests:   { color: '#00D976', glow: 'rgba(0,217,118,0.5)', label: 'guests',   icon: Users,      tab: 'guests',  noun: 'new guests',    detailTab: 'guests' },
  bookings: { color: '#5BC0EB', glow: 'rgba(91,192,235,0.5)', label: 'transactions', icon: Calendar,   tab: 'finance', noun: 'transactions', detailTab: 'transactions' },
  revenue:  { color: '#FFB454', glow: 'rgba(255,180,84,0.5)', label: 'Allure revenue',  icon: CreditCard, tab: 'finance', noun: 'Allure revenue', detailTab: 'transactions' },
  reports:  { color: '#FF6B7A', glow: 'rgba(255,107,122,0.5)', label: 'reports', icon: Flag,       tab: 'reports', noun: 'reports',       detailTab: 'reports' },
  support:  { color: '#C792EA', glow: 'rgba(199,146,234,0.5)', label: 'support', icon: LifeBuoy,   tab: 'support', noun: 'support requests', detailTab: 'support' },
};

const formatMoney = (n) => `${Number(n || 0).toFixed(2)}€`;

// Pick the most "actionable" metric in a bucket so a tap on the bar
// jumps to a single tab. Order priority = ops urgency:
// reports > support > bookings > revenue > guests.
const dominantMetric = (bucket) => {
  const priority = ['reports', 'support', 'bookings', 'revenue', 'guests'];
  for (const key of priority) {
    const value = key === 'revenue' ? bucket.revenue : bucket[key];
    if (value) return key;
  }
  return null;
};

const HourBar = ({ bucket, maxes, isNow, onOpenDetail }) => {
  const height = 56;
  const segments = ['guests', 'bookings', 'revenue', 'reports', 'support']
    .map((key) => {
      const value = key === 'revenue' ? bucket.revenue : bucket[key];
      if (!value) return null;
      const max = Math.max(0.0001, maxes[key]);
      const h = (value / max) * (height - 4);
      return { key, value, height: Math.max(2, h) };
    })
    .filter(Boolean);

  const isEmpty = segments.length === 0;
  const focusMetric = dominantMetric(bucket);
  const canClick = !isEmpty && Boolean(focusMetric) && typeof onOpenDetail === 'function';

  const tooltip = canClick
    ? `${bucket.hour_label} — ${bucket.guests} guests · ${bucket.bookings} transactions · ${formatMoney(bucket.revenue)} Allure net · ${bucket.reports} reports · ${bucket.support} support · click to drill into this hour`
    : `${bucket.hour_label} — ${bucket.guests} guests · ${bucket.bookings} transactions · ${formatMoney(bucket.revenue)} Allure net · ${bucket.reports} reports · ${bucket.support} support`;

  const handleClick = () => {
    if (!canClick) return;
    const meta = METRIC_COLORS[focusMetric];
    onOpenDetail({
      hourIso: bucket.hour_iso,
      hourLabel: bucket.hour_label,
      initialTab: meta.detailTab,
    });
  };

  return (
    <div
      data-testid={`admin-heartbeat-bar-${bucket.hour_label.replace(':', '')}`}
      role={canClick ? 'button' : undefined}
      tabIndex={canClick ? 0 : -1}
      aria-label={canClick ? `Open hour detail for ${bucket.hour_label}` : undefined}
      onClick={handleClick}
      onKeyDown={(event) => {
        if (!canClick) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleClick();
        }
      }}
      title={tooltip}
      style={{
        flex: 1,
        minWidth: 0,
        height,
        display: 'flex',
        flexDirection: 'column-reverse',
        gap: 1,
        position: 'relative',
        cursor: canClick ? 'pointer' : 'help',
        transition: 'opacity 140ms ease',
      }}
      onMouseEnter={(event) => { if (canClick) event.currentTarget.style.opacity = '0.78'; }}
      onMouseLeave={(event) => { event.currentTarget.style.opacity = '1'; }}
    >
      {isEmpty ? (
        <div style={{ height: 2, background: 'rgba(255,255,255,0.08)', borderRadius: 1, alignSelf: 'stretch' }} />
      ) : (
        segments.map((seg) => (
          <div
            key={seg.key}
            style={{
              height: `${seg.height}px`,
              background: METRIC_COLORS[seg.key].color,
              boxShadow: isNow ? `0 0 6px ${METRIC_COLORS[seg.key].glow}` : 'none',
              borderRadius: 1,
              transition: 'height 200ms ease',
            }}
          />
        ))
      )}
      {isNow ? (
        <span
          aria-hidden="true"
          className="heartbeat-pulse"
          style={{
            position: 'absolute',
            top: -7,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: '#00D976',
            boxShadow: '0 0 8px #00D976, 0 0 12px rgba(0,217,118,0.5)',
            animation: 'heartbeat-pulse 1.4s infinite ease-in-out',
          }}
        />
      ) : null}
    </div>
  );
};

const LegendDot = ({ metric, total, onNavigate }) => {
  const { color, label, icon: Icon, tab, noun } = METRIC_COLORS[metric];
  const display = metric === 'revenue' ? formatMoney(total) : total;
  const numericTotal = metric === 'revenue' ? Number(total || 0) : Number(total || 0);
  const canClick = numericTotal > 0 && typeof onNavigate === 'function';

  const handleClick = () => {
    if (!canClick) return;
    toast.info(`24h total · ${display} ${noun} — jumping to ${label}`);
    onNavigate(tab, { hour: null, metric });
  };

  return (
    <button
      type="button"
      data-testid={`admin-heartbeat-legend-${metric}`}
      onClick={handleClick}
      disabled={!canClick}
      title={canClick ? `Open ${label} tab` : `No ${label} in the last 24h`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        color: canClick ? '#FFFFFF' : 'rgba(255,255,255,0.5)',
        fontSize: 11.5,
        background: canClick ? 'rgba(255,255,255,0.04)' : 'transparent',
        border: '1px solid transparent',
        borderRadius: 9999,
        padding: canClick ? '4px 10px 4px 4px' : '4px 8px 4px 4px',
        cursor: canClick ? 'pointer' : 'default',
        transition: 'background 140ms ease, border-color 140ms ease',
        fontFamily: 'inherit',
      }}
      onMouseEnter={(event) => { if (canClick) { event.currentTarget.style.background = 'rgba(255,255,255,0.1)'; event.currentTarget.style.borderColor = 'rgba(255,255,255,0.18)'; } }}
      onMouseLeave={(event) => { event.currentTarget.style.background = canClick ? 'rgba(255,255,255,0.04)' : 'transparent'; event.currentTarget.style.borderColor = 'transparent'; }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, borderRadius: 6, background: `${color}22`, color }}>
        <Icon size={11} strokeWidth={2.5} />
      </span>
      <span style={{ fontWeight: 700, color: canClick ? '#FFFFFF' : 'rgba(255,255,255,0.55)', fontVariantNumeric: 'tabular-nums' }}>{display}</span>
      <span>{label}</span>
    </button>
  );
};

export const HeartbeatStrip = ({ headers, onNavigate }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null); // { hourIso, hourLabel, initialTab }

  const fetchHeartbeat = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/analytics/heartbeat-24h`, { headers });
      setData(res.data);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('heartbeat fetch failed', err);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchHeartbeat();
    const interval = setInterval(fetchHeartbeat, 60_000);
    return () => clearInterval(interval);
  }, [fetchHeartbeat]);

  const series = data?.series || [];
  const totals = data?.totals || { guests: 0, bookings: 0, revenue: 0, reports: 0, support: 0 };
  const maxes = {
    guests: Math.max(1, ...series.map((b) => b.guests || 0)),
    bookings: Math.max(1, ...series.map((b) => b.bookings || 0)),
    revenue: Math.max(0.01, ...series.map((b) => b.revenue || 0)),
    reports: Math.max(1, ...series.map((b) => b.reports || 0)),
    support: Math.max(1, ...series.map((b) => b.support || 0)),
  };
  const allTotalsZero = totals.guests === 0 && totals.bookings === 0 && totals.revenue === 0 && totals.reports === 0 && totals.support === 0;

  return (
    <div
      data-testid="admin-heartbeat-strip"
      style={{
        background: 'linear-gradient(135deg, #0F1115 0%, #1A1D24 100%)',
        borderRadius: 20,
        padding: 18,
        color: '#FFFFFF',
        boxShadow: '0 12px 32px rgba(15,17,21,0.18)',
        position: 'relative',
        overflow: 'hidden',
        boxSizing: 'border-box',
        maxWidth: '100%',
      }}
    >
      <style>{`
        @keyframes heartbeat-pulse {
          0%, 100% { transform: translateX(-50%) scale(1); opacity: 1; }
          50% { transform: translateX(-50%) scale(1.6); opacity: 0.6; }
        }
      `}</style>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <div>
          <p style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 18, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Activity size={16} color="#00D976" />
            Last 24 hours
          </p>
          <p style={{ margin: '4px 0 0', fontSize: 11.5, color: 'rgba(255,255,255,0.55)' }}>
            {allTotalsZero
              ? 'Quiet — nothing happened in the last day. Strip wakes up automatically when activity arrives.'
              : 'Tap any bar or legend chip to jump straight into the matching admin tab. Blinking dot is the current hour.'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <LegendDot metric="guests" total={totals.guests} onNavigate={onNavigate} />
          <LegendDot metric="bookings" total={totals.bookings} onNavigate={onNavigate} />
          <LegendDot metric="revenue" total={totals.revenue} onNavigate={onNavigate} />
          <LegendDot metric="reports" total={totals.reports} onNavigate={onNavigate} />
          <LegendDot metric="support" total={totals.support} onNavigate={onNavigate} />
        </div>
      </div>

      <div
        data-testid="admin-heartbeat-bars"
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 2,
          height: 56,
          marginBottom: 6,
          paddingTop: 8,
        }}
      >
        {loading && series.length === 0 ? (
          <div style={{ flex: 1, height: 2, background: 'rgba(255,255,255,0.06)', alignSelf: 'center', borderRadius: 1 }} />
        ) : (
          series.map((bucket, idx) => (
            <HourBar
              key={bucket.hour_iso}
              bucket={bucket}
              maxes={maxes}
              isNow={idx === series.length - 1}
              onOpenDetail={setDetail}
            />
          ))
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9.5, color: 'rgba(255,255,255,0.4)', letterSpacing: 0.4, fontVariantNumeric: 'tabular-nums' }}>
        <span>{series[0]?.hour_label || '--:--'}</span>
        <span>{series[Math.floor(series.length / 2)]?.hour_label || ''}</span>
        <span style={{ color: '#00D976', fontWeight: 700 }}>{series[series.length - 1]?.hour_label || 'now'}</span>
      </div>

      <HeartbeatHourDetailModal
        open={Boolean(detail)}
        hourIso={detail?.hourIso}
        initialTab={detail?.initialTab}
        headers={headers}
        onClose={() => setDetail(null)}
        onNavigate={onNavigate}
      />
    </div>
  );
};

export default HeartbeatStrip;
